import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../components/auth/AuthContext';
import { useAccount } from './AccountContext';
import { api } from '../lib/api';

const BrandIdentityContext = createContext();

export const useBrandIdentity = () => {
  const context = useContext(BrandIdentityContext);
  if (!context) {
    throw new Error('useBrandIdentity must be used within BrandIdentityProvider');
  }
  return context;
};

// Mapeamento de fontes populares para uso no agente
export const POPULAR_FONTS = {
  "montserrat": "montserrat-bold",
  "montserrat bold": "montserrat-bold",
  "montserrat black": "montserrat-black",
  "montserrat light": "montserrat-light",
  "inter": "Inter-Bold",
  "inter bold": "Inter-Bold",
  "inter black": "Inter-Black",
  "poppins": "poppins-bold",
  "poppins bold": "poppins-bold",
  "poppins black": "poppins-black",
  "roboto": "roboto-bold",
  "roboto bold": "roboto-bold",
  "open sans": "opensans-bold",
  "open sans bold": "opensans-bold",
  "lato": "lato-bold",
  "lato bold": "lato-bold",
  "raleway": "raleway-bold",
  "raleway bold": "raleway-bold",
  "nunito": "nunito-bold",
  "oswald": "oswald-bold",
  "oswald bold": "oswald-bold",
  "playfair": "playfairdisplay-bold",
  "playfair display": "playfairdisplay-bold",
  "bangers": "bangers-regular",
  "bungee": "bungee-regular",
  "lilita one": "lilitaone",
  "lobster": "lobster-regular",
  "pacifico": "pacifico-regular",
  "barlow condensed": "barlowcondensed-bold",
  "space mono": "spacemono-bold",
  "ibm plex": "ibmplexsans-bold",
  "source sans": "sourcesanspro-bold",
};

// Função para obter o nome do arquivo da fonte para o agente
export const getFontFileName = (fontName) => {
  if (!fontName) return 'Inter-Bold';
  const normalized = fontName.toLowerCase().trim();
  return POPULAR_FONTS[normalized] || 'Inter-Bold';
};

export const BrandIdentityProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const { activeAccount } = useAccount();
  const [brandIdentity, setBrandIdentity] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Cores em HEX ou TEXTO
  const [colors, setColors] = useState({
    primary: '#F59E0B',
    secondary: '#000000'
  });
  
  // Fontes com nome legível (será convertido para filename do agente quando necessário)
  const [fonts, setFonts] = useState({
    primary: 'inter',
    secondary: 'poppins'
  });
  
  const [logoUrl, setLogoUrl] = useState(null);
  const [aboutBusiness, setAboutBusiness] = useState('');

  const loadBrandIdentity = useCallback(async () => {
    setIsLoading(true);
    try {
      let data = null;
      if (activeAccount?.brand_identity && Object.keys(activeAccount.brand_identity).length > 0) {
        data = activeAccount.brand_identity;
      } else {
        try {
          const response = await api.get('/api/user/brand-identity');
          data = response.data?.data || response.data;
        } catch (err) {
          data = null;
        }
      }
      
      if (data) {
        setBrandIdentity(data);
        
        if (data.colors) {
          const newColors = {
            primary: data.colors.primary || '#F59E0B',
            secondary: data.colors.secondary || '#000000'
          };
          setColors(newColors);
        }
        
        if (data.fonts) {
          const newFonts = {
            primary: data.fonts.primary || 'inter',
            secondary: data.fonts.secondary || 'poppins'
          };
          setFonts(newFonts);
        }
        
        if (data.logo_url) {
          setLogoUrl(data.logo_url);
        }
        
        if (data.about_business) {
          setAboutBusiness(data.about_business);
        } else {
          // Fallback: tentar combinar campos antigos se existirem
          const legacyAbout = [
            data.positioning,
            data.values,
            data.persona,
            data.guidelines
          ].filter(Boolean).join(' ').substring(0, 200);
          setAboutBusiness(legacyAbout);
        }
      }
    } catch (err) {
      if (err.response?.status !== 404) {
        console.error('Erro ao carregar identidade visual:', err);
      }
    } finally {
      setIsLoading(false);
    }
  }, [activeAccount]);

  useEffect(() => {
    if (currentUser) {
      loadBrandIdentity();
    } else {
      // Reset para padrão quando não há usuário
      setColors({
        primary: '#F59E0B',
        secondary: '#000000'
      });
      setFonts({
        primary: 'inter',
        secondary: 'poppins'
      });
      setLogoUrl(null);
      setAboutBusiness('');
      setIsLoading(false);
    }
  }, [currentUser, activeAccount, loadBrandIdentity]);

  // Escutar eventos de atualização de identidade
  useEffect(() => {
    const handleUpdate = () => {
      if (currentUser) {
        loadBrandIdentity();
      }
    };
    
    window.addEventListener('brandIdentityUpdated', handleUpdate);
    return () => {
      window.removeEventListener('brandIdentityUpdated', handleUpdate);
    };
  }, [currentUser, loadBrandIdentity]);

  const updateBrandIdentity = useCallback((newIdentity) => {
    setBrandIdentity(newIdentity);
    
    if (newIdentity.colors) {
      const newColors = {
        primary: newIdentity.colors.primary || '#F59E0B',
        secondary: newIdentity.colors.secondary || '#000000'
      };
      setColors(newColors);
    }
    
    if (newIdentity.fonts) {
      const newFonts = {
        primary: newIdentity.fonts.primary || 'inter',
        secondary: newIdentity.fonts.secondary || 'poppins'
      };
      setFonts(newFonts);
    }
    
    if (newIdentity.logo_url !== undefined) {
      setLogoUrl(newIdentity.logo_url);
    }
    
    if (newIdentity.about_business !== undefined) {
      setAboutBusiness(newIdentity.about_business);
    }
  }, []);

  // Fontes convertidas para nomes de arquivo (para uso no agente)
  const fontsForAgent = useMemo(() => ({
    primary: getFontFileName(fonts.primary),
    secondary: getFontFileName(fonts.secondary)
  }), [fonts]);

  const contextValue = useMemo(() => ({
    brandIdentity: {
      ...brandIdentity,
      about_business: aboutBusiness
    },
    colors,
    fonts,
    fontsForAgent, // Nomes de arquivo das fontes para o agente
    logoUrl,
    aboutBusiness,
    isLoading,
    updateBrandIdentity,
    reloadBrandIdentity: loadBrandIdentity,
    // Utilitários exportados
    getFontFileName,
    POPULAR_FONTS
  }), [brandIdentity, colors, fonts, fontsForAgent, logoUrl, aboutBusiness, isLoading, updateBrandIdentity, loadBrandIdentity]);

  return (
    <BrandIdentityContext.Provider value={contextValue}>
      {children}
    </BrandIdentityContext.Provider>
  );
};